/**
 * @fileoverview Payload builders for UserDetailModal
 * Functions to prepare form data and build API payloads
 */

/**
 * Prepares edit form data from user object
 * @param {object} user - User data
 * @returns {object} Form data
 */
export const prepareEditFormData = (user) => ({
  user_name: user.user_name || "",
  user_email: user.user_email || "",
  user_phone: user.user_phone || "",
  user_language: user.user_language || "",
  status: user.status ?? 1,
  roles: (user.roles || []).map((r) => r.role_name),
  cpf: user.person?.cpf || "",
  status_reason: user.person?.status_reason || "",
  account_status: user.is_blocked ? -1 : (user.person?.account_status ?? null),
  crm: user.doctor?.crm || "",
  crm_state: user.doctor?.crm_state || "",
  // Additional CRMs from doctor.additional_crms
  crms: (user.doctor?.additional_crms || []).map(c => ({ crm: c.crm, crm_state: c.crm_state })),
  crf: user.pharmacist?.crf || "",
  crf_state: user.pharmacist?.crf_state || "",
  address: user.address?.address || "",
  address_number: user.address?.number || "",
  zip_code: user.address?.zip_code || "",
  city: user.address?.city || "",
  address_state: user.address?.state || "",
  // All user types now support multiple institutions
  institution_pks: user.institutions?.map(i => i.pk) || [],
  // Keep institution_pk for backwards compatibility (first institution)
  institution_pk: user.institutions?.[0]?.pk || null,
});

/**
 * Builds update payload with only changed fields
 * @param {object} editFormData - Current form data
 * @param {object} originalUser - Original user data
 * @returns {object} Payload with changed fields
 */
export const buildUpdatePayload = (editFormData, originalUser) => {
  const payload = {};

  if (editFormData.user_name !== originalUser.user_name) {
    payload.user_name = editFormData.user_name;
  }
  if (editFormData.user_email !== (originalUser.user_email || "")) {
    payload.user_email = editFormData.user_email || null;
    // Always update user_login with the same value as email
    payload.user_login = editFormData.user_email || null;
  }
  // Compare phone numbers without formatting
  const cleanEditPhone = editFormData.user_phone ? editFormData.user_phone.replace(/\D/g, "") : "";
  const cleanOriginalPhone = originalUser.user_phone || "";
  if (cleanEditPhone !== cleanOriginalPhone) {
    payload.user_phone = cleanEditPhone || null;
  }
  if (editFormData.user_language !== originalUser.user_language) {
    payload.user_language = editFormData.user_language;
  }
  if (editFormData.status !== originalUser.status) {
    payload.status = editFormData.status;
  }

  const originalRoles = (originalUser.roles || []).map((r) => r.role_name).sort().join(",");
  const newRoles = (editFormData.roles || []).sort().join(",");
  if (originalRoles !== newRoles) {
    payload.roles = editFormData.roles;
  }

  if (originalUser.person) {
    if (editFormData.cpf !== originalUser.person.cpf) {
      payload.cpf = editFormData.cpf.replace(/\D/g, "");
    }
    if (editFormData.status_reason !== (originalUser.person.status_reason || "")) {
      payload.status_reason = editFormData.status_reason || null;
    }
    // Compare against -1 (blocked) or the real account_status
    const originalAccountStatus = originalUser.is_blocked ? -1 : (originalUser.person.account_status ?? null);
    if (editFormData.account_status !== originalAccountStatus) {
      if (originalUser.is_blocked && editFormData.account_status !== -1) {
        // Changing from Blocked to another status: unblock + set new account_status
        payload.unblock_user = true;
        payload.account_status = editFormData.account_status;
      } else if (!originalUser.is_blocked && editFormData.account_status === -1) {
        // Changing to Blocked: block the user
        payload.block_user = true;
      } else if (editFormData.account_status !== -1) {
        // Normal account_status change (not involving block)
        payload.account_status = editFormData.account_status;
      }
    }
  }

  if (originalUser.doctor) {
    if (editFormData.crm !== originalUser.doctor.crm) {
      payload.crm = editFormData.crm;
    }
    if (editFormData.crm_state !== originalUser.doctor.crm_state) {
      payload.crm_state = editFormData.crm_state;
    }
    // Compare additional CRMs
    const originalCrms = (originalUser.doctor?.additional_crms || [])
      .map(c => `${c.crm}|${c.crm_state}`)
      .sort()
      .join(",");
    const newCrms = (editFormData.crms || [])
      .map(c => `${c.crm}|${c.crm_state}`)
      .sort()
      .join(",");
    if (originalCrms !== newCrms) {
      payload.crms = editFormData.crms || [];
    }
  }

  if (originalUser.pharmacist) {
    if (editFormData.crf !== originalUser.pharmacist.crf) {
      payload.crf = editFormData.crf;
    }
    if (editFormData.crf_state !== originalUser.pharmacist.crf_state) {
      payload.crf_state = editFormData.crf_state;
    }
  }

  // Address fields - always include for external users (person/doctor/pharmacist)
  if (originalUser.person) {
    const origAddress = originalUser.address || {};
    if (editFormData.address !== (origAddress.address || "")) {
      payload.address = editFormData.address;
    }
    if (editFormData.address_number !== (origAddress.number || "")) {
      payload.address_number = editFormData.address_number;
    }
    if (editFormData.zip_code !== (origAddress.zip_code || "")) {
      payload.zip_code = editFormData.zip_code;
    }
    if (editFormData.city !== (origAddress.city || "")) {
      payload.city = editFormData.city;
    }
    if (editFormData.address_state !== (origAddress.state || "")) {
      payload.address_state = editFormData.address_state;
    }
  }

  // Include institution_pks if institutions have changed (all user types now support multiple)
  if (originalUser.person || originalUser.doctor || originalUser.pharmacist) {
    const originalPks = (originalUser.institutions?.map(i => i.pk) || []).sort().join(",");
    const newPks = (editFormData.institution_pks || []).sort().join(",");
    if (originalPks !== newPks) {
      payload.institution_pks = editFormData.institution_pks || [];
      // Also set institution_pk for backwards compatibility
      payload.institution_pk = editFormData.institution_pks?.length > 0 ? editFormData.institution_pks[0] : null;
    }
  }

  return payload;
};
